import { fontAttrs } from "@/components/tools/printables/page-kit";
import type {
  HandwritingLayout,
  HandwritingPage,
  PlacedText,
  TraceStyle,
} from "@/lib/tool-kit/printables/handwriting";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { type TextMeasure, textWidthMm } from "@/lib/tool-kit/printables/text";

const HEADER_FONT = { kind: "sans", bold: true } as const;
const LABEL_FONT = { kind: "sans" } as const;
const INK = "#1a1a1a";

function traceFill(item: PlacedText, style: TraceStyle): string {
  if (item.kind === "model") {
    return INK;
  }
  return style === "pale" ? "#c4c4c4" : "none";
}

/** Title, and the Name and Date lines, at the top of page 1. */
export function HandwritingHeader({
  geometry,
  title,
  nameLine,
  nameLabel,
  dateLabel,
  measure,
}: {
  geometry: PageGeometry;
  title: string;
  nameLine: boolean;
  nameLabel: string;
  dateLabel: string;
  measure: TextMeasure;
}) {
  const nameWidth = textWidthMm(measure, nameLabel, LABEL_FONT, 4);
  const dateWidth = textWidthMm(measure, dateLabel, LABEL_FONT, 4);
  const left = geometry.left;
  const right = geometry.left + geometry.contentWidth;
  const titleY = geometry.top + 6;
  const lineY = geometry.top + (title ? 17 : 7);
  const dateX = right - 52;
  return (
    <g fill={INK}>
      {title ? (
        <text fontSize={7} x={left} y={titleY} {...fontAttrs(HEADER_FONT)}>
          {title}
        </text>
      ) : null}
      {nameLine ? (
        <g {...fontAttrs(LABEL_FONT)} fontSize={4}>
          <text x={left} y={lineY}>
            {nameLabel}
          </text>
          <line
            stroke={INK}
            strokeWidth={0.25}
            x1={Math.round((left + nameWidth + 2) * 100) / 100}
            x2={dateX - 8}
            y1={lineY + 0.8}
            y2={lineY + 0.8}
          />
          <text x={dateX} y={lineY}>
            {dateLabel}
          </text>
          <line
            stroke={INK}
            strokeWidth={0.25}
            x1={Math.round((dateX + dateWidth + 2) * 100) / 100}
            x2={right}
            y1={lineY + 0.8}
            y2={lineY + 0.8}
          />
        </g>
      ) : null}
    </g>
  );
}

/** One page of primary-ruled lines with the traced words on them. */
export function HandwritingSheet({
  layout,
  page,
  lineColor,
  traceStyle,
}: {
  layout: HandwritingLayout;
  page: HandwritingPage;
  lineColor: string;
  traceStyle: TraceStyle;
}) {
  const { x1, x2, height, fontSize, font } = layout;
  const strokeWidth = Math.max(0.3, Math.round(fontSize * 0.014 * 100) / 100);
  const dash = `${Math.round(fontSize * 0.05 * 100) / 100} ${Math.round(fontSize * 0.04 * 100) / 100}`;
  return (
    <g>
      {page.rows.map((row) => (
        <g key={row.y}>
          <line
            stroke={lineColor}
            strokeWidth={0.3}
            x1={x1}
            x2={x2}
            y1={row.y}
            y2={row.y}
          />
          <line
            stroke={lineColor}
            strokeDasharray="2 1.5"
            strokeWidth={0.25}
            x1={x1}
            x2={x2}
            y1={row.y + height / 2}
            y2={row.y + height / 2}
          />
          <line
            stroke={lineColor}
            strokeWidth={0.3}
            x1={x1}
            x2={x2}
            y1={row.y + height}
            y2={row.y + height}
          />
          {row.texts.map((item) => (
            <text
              fill={traceFill(item, traceStyle)}
              fontSize={fontSize}
              key={`${item.kind}-${item.x}`}
              stroke={
                item.kind === "trace" && traceStyle === "dashed"
                  ? "#444444"
                  : "none"
              }
              strokeDasharray={
                item.kind === "trace" && traceStyle === "dashed"
                  ? dash
                  : undefined
              }
              strokeLinejoin="round"
              strokeWidth={strokeWidth}
              x={Math.round(item.x * 100) / 100}
              y={row.y + height}
              {...fontAttrs(font)}
            >
              {item.text}
            </text>
          ))}
        </g>
      ))}
    </g>
  );
}
